export interface AgentConfig {
  key: string;
  runningLabel: string;
  summary: string;
  /**
   * Lane is filled exclusively by the scan stream.
   * When no stream is present it is pre-marked done so bootstrap-only
   * flows don't show it as blocked.
   */
  scanOnly?: boolean;
  /**
   * Feature-flag key that gates this agent's visibility.
   * If the matching flag is false the lane is hidden.
   * e.g. 'hasImages' | 'hasReviews'
   * Add new flags to FirstActionFlowProps + featureFlags in index.tsx.
   */
  visibilityFlag?: string;
}

export type AgentLaneState = {
  phase: 'queued' | 'running' | 'done' | 'failed';
  progress: number;
  message: string;
  /** Every step in the lane was skipped: settled, but nothing was produced. */
  skipped?: boolean;
  /** Optional per-lane upper bound for creep; overrides global cap. */
  cap?: number;
  /** Accumulated streaming entity text (persona/voice/style body). */
  streamText?: string;
  /** Friendly title of the entity currently streaming (e.g. persona name). */
  entityTitle?: string;
  /** Per-persona sub-rows for parallel persona generation in the audience lane. */
  personas?: PersonaRowState[];
  /** Icons shown alongside the lane — site favicons for knowledge, platform logos for trust. */
  icons?: Array<{ url: string; label: string }>;
  steps?: BuildSubStep[];
};

export interface BuildSubStep {
  key: string;
  text: string;
  done: boolean;
}

export interface PersonaRowState {
  index: number;
  name: string;
  progress: number;
  phase: 'queued' | 'running' | 'done' | 'failed';
}

/** Maps th-text-gen agentType → UI lane key */
export const AGENT_TYPE_MAP: Record<string, string> = {
  'site-page-fetcher':      'knowledge',
  'social-proof-fetcher':   'trust',
  'ai-citation-checker':    'voice',
  'google-citation-checker':'visibility',
  'image-curator':          'brand',
  'image-fetcher':          'importMedia',
  'brand-synthesizer':      'audience',
  'content-strategist':     'model',
};

export const PERSONA_LANE_KEY = 'audience';

export const PARALLEL_AGENTS: AgentConfig[] = [
  { key: 'foundation',   runningLabel: 'Setting up your workspace…',       summary: 'Ready · brand entities created' },
  { key: 'voice',        runningLabel: 'Listening for your brand voice…',  summary: 'Calibrated · warm, considered, unhurried' },
  { key: 'visibility',   runningLabel: 'Checking Google search visibility…', summary: 'Checked · search presence mapped',         scanOnly: true },
  { key: 'trust',        runningLabel: 'Reading reviews and credentials…', summary: 'Verified · reviews and credentials indexed', scanOnly: true, visibilityFlag: 'hasReviews' },
  { key: 'recognitions', runningLabel: 'Finding your awards and recognitions…', summary: 'Collected · awards and recognitions indexed' },
  { key: 'audience',     runningLabel: 'Profiling your audience…',         summary: 'Defined · audience personas identified' },
  { key: 'brand',        runningLabel: 'Reading your brand cues…',         summary: 'Captured · palette and voice motifs',        scanOnly: true },
  { key: 'knowledge',    runningLabel: 'Indexing your content archive…',   summary: 'Indexed · nodes and edges mapped',            scanOnly: true },
  { key: 'importMedia',  runningLabel: 'Curating site imagery…',           summary: 'Imported · images ready in media library',    scanOnly: true, visibilityFlag: 'hasImages' },
  { key: 'syncProducts', runningLabel: 'Syncing your product catalogue…',  summary: 'Synced · product catalogue ready' },
  { key: 'siteHealth',   runningLabel: 'Analysing your site health…',      summary: 'Analysed · site health report ready' },
  { key: 'model',        runningLabel: 'Compiling your writing style…',    summary: 'Compiled · writing style ready' },
];

export const SEQUENTIAL: AgentConfig[] = [
  { key: 'contentDraft', runningLabel: 'Drafting in your voice…', summary: 'Ready · first draft complete' },
];

export const ALL_AGENTS = [...PARALLEL_AGENTS, ...SEQUENTIAL];

/** Lane keys that are scan-stream-only — derived from config, not hardcoded. */
export const SCAN_ONLY_LANE_KEYS = ALL_AGENTS.filter(a => a.scanOnly).map(a => a.key);

export const SCAN_OWNED_LANE_KEYS: string[] = Array.from(
  new Set<string>([...Object.values(AGENT_TYPE_MAP), ...SCAN_ONLY_LANE_KEYS]),
);

export function agentByKey(key: string): AgentConfig | undefined {
  return ALL_AGENTS.find(a => a.key === key);
}